import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { allLocations } from '../data/routesData';
import SearchableSelect from './SearchableSelect';
import TravelDatePicker from './TravelDatePicker';
import {
  dateToYmd,
  formatDateDmy,
  parseDateDmy,
  todayInTanzania,
} from '../utils/travelDate';
import { useBooking } from '../booking/BookingContext';

const POPULAR = [
  { from: 'songea', to: 'dar-es-salaam', label: 'Songea → DSM' },
  { from: 'songea', to: 'chunya', label: 'Songea → Chunya' },
  { from: 'songea', to: 'mwanza', label: 'Songea → Mwanza' },
  { from: 'songea', to: 'dodoma', label: 'Songea → Dodoma' },
];

const SearchForm = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const booking = useBooking();
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    // Match the picker UI default (today in Tanzania) so submit does not
    // ask for a date that already looks selected.
    date: formatDateDmy(todayInTanzania()),
    passengers: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const cityOptions = useMemo(() => allLocations, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');
    const toParam = urlParams.get('to');

    if (fromParam || toParam) {
      setFormData((prev) => ({
        ...prev,
        from: fromParam || prev.from,
        to: toParam || prev.to,
        date: prev.date || formatDateDmy(todayInTanzania()),
      }));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSelectValueChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const handleDateChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      date: value,
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    const origin = formData.from;
    const destination = formData.to;

    if (!origin) {
      setError(t('selectDepartureError'));
      return false;
    }
    if (!destination) {
      setError(t('selectDestinationError'));
      return false;
    }
    if (origin === destination) {
      setError(t('differentCitiesError'));
      return false;
    }
    if (!formData.date) {
      setError(t('selectDateError'));
      return false;
    }
    const parsedDate = parseDateDmy(formData.date);
    if (!parsedDate) {
      setError(t('invalidDateError'));
      return false;
    }
    const today = todayInTanzania();
    if (parsedDate < today) {
      setError(t('pastDateError'));
      return false;
    }
    if (!formData.passengers) {
      setError(t('selectPassengersError'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      booking.setSearch({
        origin: formData.from,
        destination: formData.to,
        date: dateToYmd(parseDateDmy(formData.date)),
        passengers: parseInt(formData.passengers, 10) || 1,
      });
      navigate('/book/trips');
    } finally {
      setIsLoading(false);
    }
  };

  const fromOptions = useMemo(
    () => cityOptions.filter((o) => o.value !== formData.to),
    [cityOptions, formData.to]
  );
  const toOptions = useMemo(
    () => cityOptions.filter((o) => o.value !== formData.from),
    [cityOptions, formData.from]
  );

  return (
    <div id="search-form" className="w-full">
      <form
        onSubmit={handleSubmit}
        className="rounded-[20px] border-[1.5px] border-[#E91E63] bg-white p-4 shadow-[0_6px_18px_rgba(233,30,99,0.12)]"
      >
        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-[#FFF1F0] px-3 py-2">
            <p className="font-body text-sm font-semibold text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <SearchableSelect
            classic
            label={t('from')}
            name="from"
            value={formData.from}
            onChange={(value) => handleSelectValueChange('from', value)}
            options={fromOptions}
            placeholder={t('selectCity') || t('selectDeparture')}
            isOpen={openDropdown === 'from'}
            onToggle={() =>
              setOpenDropdown((prev) => (prev === 'from' ? null : 'from'))
            }
            onClose={() => setOpenDropdown(null)}
          />

          <SearchableSelect
            classic
            label={t('to')}
            name="to"
            value={formData.to}
            onChange={(value) => handleSelectValueChange('to', value)}
            options={toOptions}
            placeholder={t('selectCity') || t('selectDestination')}
            isOpen={openDropdown === 'to'}
            onToggle={() =>
              setOpenDropdown((prev) => (prev === 'to' ? null : 'to'))
            }
            onClose={() => setOpenDropdown(null)}
          />

          <div className="grid grid-cols-2 gap-3">
            <TravelDatePicker
              classic
              label={t('travelDate')}
              value={formData.date}
              onChange={handleDateChange}
              minDate={todayInTanzania()}
            />

            <label className="block">
              <span className="mb-1 block text-[11px] font-body font-extrabold uppercase tracking-wide text-[#E91E63]">
                {t('passengers')}
              </span>
              <select
                name="passengers"
                value={formData.passengers}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    passengers: e.target.value,
                  }));
                  if (error) setError('');
                }}
                className="w-full appearance-none rounded-xl border-2 border-[#F8BBD0] bg-white px-3 py-2.5 font-body text-sm font-semibold text-[#111827] outline-none transition focus:border-[#E91E63]"
              >
                <option value="">{t('selectPassengers')}</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={String(n)}>
                    {n}{' '}
                    {n === 1 ? t('passenger') : t('passengersPlural')}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <p className="mt-4 mb-2.5 font-body text-[15px] font-extrabold text-[#E91E63]">
          {t('popularRoutes')}
        </p>
        <div className="grid grid-cols-2 gap-2.5">
          {POPULAR.map((route) => (
            <button
              key={route.label}
              type="button"
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  from: route.from,
                  to: route.to,
                  date: prev.date || formatDateDmy(todayInTanzania()),
                  passengers: prev.passengers || '1',
                }));
                setError('');
              }}
              className="rounded-xl bg-[#E91E63] px-2 py-3 text-center font-body text-xs font-bold leading-tight text-white transition hover:bg-[#C2185B]"
            >
              {route.label}
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-4 w-full rounded-[14px] bg-[#FF9800] px-4 py-4 font-body text-[15px] font-extrabold tracking-wider text-white transition hover:bg-[#FB8C00] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {isLoading ? t('searching') : t('searchSafari')}
        </button>
      </form>
    </div>
  );
};

export default SearchForm;
